import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ComboBox, useDialog } from '@gokrew/ui';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@gokrew/ui/components/table';
import { ArrowRightLeft, Trash2, Upload } from 'lucide-react';
import {
  CRM_LEAD_STATUSES,
  convertCrmLead,
  deleteCrmLead,
  getCrmLeads,
  setCrmLeadStatus,
  type CrmLead,
  type CrmLeadStatus,
} from '@/api/crm';
import { useCrmFormat } from './crm-format';
import { crmRowInteraction } from './crm-row-interaction';
import { CrmButton, CrmEmpty, CrmError, CrmPagination } from './CrmShared';
import { CrmDeleteDialog } from './CrmDeleteDialog';
import { CrmLeadImport } from './CrmLeadImport';
import { CrmLeadEditor } from './CrmLeadEditor';
import styles from './Crm.module.css';

const leadName = (lead: CrmLead) =>
  [lead.firstName, lead.lastName].filter(Boolean).join(' ');

/** One lead: its details, status, conversion to an opportunity, deletion. */
const CrmLeadPanel = ({
  lead,
  dialogId,
  onDeal,
}: {
  lead: CrmLead;
  dialogId?: string;
  onDeal: (id: string) => void;
}) => {
  const { t } = useCrmFormat();
  const { openDialog, closeDialog } = useDialog();
  const client = useQueryClient();
  const [current, setCurrent] = useState(lead);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const refresh = () => client.invalidateQueries({ queryKey: ['crm'] });
  const changeStatus = async (status: CrmLeadStatus) => {
    setBusy(true);
    setError('');
    try {
      setCurrent(await setCrmLeadStatus(current, status));
      await refresh();
    } catch {
      setError(t('crm.conflict'));
    } finally {
      setBusy(false);
    }
  };
  const convert = async () => {
    setBusy(true);
    setError('');
    try {
      const { dealId } = await convertCrmLead(current);
      await refresh();
      if (dialogId) closeDialog(dialogId);
      onDeal(dealId);
    } catch {
      setError(t('crm.leads.convert_error'));
    } finally {
      setBusy(false);
    }
  };
  const facts: [string, string | number | null][] = [
    ['companyName', current.companyName],
    ['title', current.title],
    ['email', current.email],
    ['phone', current.phone],
    [
      'city',
      [current.city, current.province, current.country]
        .filter(Boolean)
        .join(', '),
    ],
    ['estimatedEmployees', current.estimatedEmployees],
    ['website', current.website],
    ['linkedinUrl', current.linkedinUrl],
    ['source', current.source],
    ['notes', current.notes],
  ];
  return (
    <div className={styles.form}>
      <dl className={styles.leadFacts}>
        {facts
          .filter(([, value]) => value !== null && value !== '')
          .map(([key, value]) => (
            <div key={key}>
              <dt>{t(`crm.leads.fields.${key}`)}</dt>
              <dd>{value}</dd>
            </div>
          ))}
      </dl>
      {current.status !== 'converted' && (
        <label>
          {t('crm.leads.status')}
          <ComboBox
            size="sm"
            allowUnselect={false}
            disabled={busy}
            value={current.status}
            options={CRM_LEAD_STATUSES.filter((s) => s !== 'converted').map(
              (value) => ({ value, label: t(`crm.leads.statuses.${value}`) })
            )}
            onChange={(value) => value && void changeStatus(value)}
          />
        </label>
      )}
      {error && (
        <p role="alert" className={styles.error}>
          {error}
        </p>
      )}
      <div className={styles.actions}>
        <CrmButton
          type="button"
          variant="destructive"
          IconPrefix={Trash2}
          className={styles.editorDelete}
          disabled={busy}
          onClick={() =>
            openDialog({
              title: t('crm.leads.delete_title'),
              content: (
                <CrmDeleteDialog
                  description={t('crm.leads.delete_description', {
                    name: leadName(current) || current.email || '',
                  })}
                  conflictKey="crm.delete_changed"
                  onDelete={() => deleteCrmLead(current)}
                  editorDialogId={dialogId}
                />
              ),
            })
          }
        >
          {t('crm.delete')}
        </CrmButton>
        {current.convertedDealId ? (
          <CrmButton
            type="button"
            variant="adminPrimary"
            onClick={() => {
              if (dialogId) closeDialog(dialogId);
              onDeal(current.convertedDealId!);
            }}
          >
            {t('crm.leads.open_deal')}
          </CrmButton>
        ) : (
          <CrmButton
            type="button"
            variant="adminPrimary"
            IconPrefix={ArrowRightLeft}
            disabled={busy}
            onClick={() => void convert()}
          >
            {t('crm.leads.convert')}
          </CrmButton>
        )}
      </div>
    </div>
  );
};

export const useLeadCreate = () => {
  const { t } = useCrmFormat();
  const { openDialog } = useDialog();
  return () =>
    openDialog({
      title: t('crm.leads.add'),
      containerClass: styles.editorDialog,
      content: <CrmLeadEditor />,
    });
};

export const useLeadImport = () => {
  const { t } = useCrmFormat();
  const { openDialog } = useDialog();
  return () =>
    openDialog({
      title: t('crm.leads.import_title'),
      containerClass: styles.editorDialog,
      content: <CrmLeadImport />,
    });
};

export const CrmLeads = ({
  search,
  onDeal,
}: {
  search: string;
  onDeal: (id: string) => void;
}) => {
  const { t, date } = useCrmFormat();
  const { openDialog } = useDialog();
  const importLeads = useLeadImport();
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<CrmLeadStatus | 'all'>('all');
  const leads = useQuery({
    queryKey: ['crm', 'leads', search, status, page],
    queryFn: () =>
      getCrmLeads({
        search,
        page,
        ...(status === 'all' ? {} : { status }),
      }),
  });
  const open = (lead: CrmLead) =>
    openDialog({
      title: leadName(lead) || lead.email || lead.companyName,
      containerClass: styles.editorDialog,
      content: <CrmLeadPanel lead={lead} onDeal={onDeal} />,
    });
  return (
    <>
      <div className={styles.listMeta}>
        <span>{t('crm.leads.count', { count: leads.data?.total ?? 0 })}</span>
        <CrmButton variant="outline" IconPrefix={Upload} onClick={importLeads}>
          {t('crm.leads.import_title')}
        </CrmButton>
        <div className={styles.filterChips} role="group">
          {(['all', ...CRM_LEAD_STATUSES] as const).map((value) => (
            <button
              key={value}
              type="button"
              aria-pressed={status === value}
              className={status === value ? styles.feedTabActive : ''}
              onClick={() => {
                setStatus(value);
                setPage(1);
              }}
            >
              {t(
                value === 'all'
                  ? 'crm.leads.all'
                  : `crm.leads.statuses.${value}`
              )}
            </button>
          ))}
        </div>
      </div>
      {leads.isPending ? (
        <p role="status" className={styles.loading}>
          {t('crm.loading')}
        </p>
      ) : leads.isError ? (
        <CrmError retry={() => void leads.refetch()} />
      ) : !leads.data.items.length ? (
        <div className={styles.panel}>
          <CrmEmpty
            title={t(search ? 'crm.no_results' : 'crm.leads.empty_title')}
            description={t('crm.leads.empty')}
            action={
              <CrmButton
                variant="adminPrimary"
                IconPrefix={Upload}
                onClick={importLeads}
              >
                {t('crm.leads.import_title')}
              </CrmButton>
            }
          />
        </div>
      ) : (
        <>
          <div className={styles.tableWrap}>
            <Table>
              <TableHeader>
                <TableRow>
                  {[
                    'name',
                    'companyName',
                    'title',
                    'email',
                    'city',
                    'status',
                    'source',
                    'added',
                  ].map((key) => (
                    <TableHead key={key}>
                      {t(`crm.leads.fields.${key}`)}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {leads.data.items.map((lead) => (
                  <TableRow
                    key={lead.id}
                    className={styles.clickableRow}
                    aria-label={t('crm.leads.open', {
                      name: leadName(lead) || lead.email || lead.companyName,
                    })}
                    {...crmRowInteraction(() => open(lead))}
                  >
                    <TableCell>
                      <strong>{leadName(lead) || t('crm.unknown')}</strong>
                    </TableCell>
                    <TableCell>
                      {lead.companyName || t('crm.unknown')}
                    </TableCell>
                    <TableCell>{lead.title || t('crm.unknown')}</TableCell>
                    <TableCell>{lead.email || t('crm.unknown')}</TableCell>
                    <TableCell>{lead.city || t('crm.unknown')}</TableCell>
                    <TableCell>
                      <span className={styles.chip}>
                        {t(`crm.leads.statuses.${lead.status}`)}
                      </span>
                    </TableCell>
                    <TableCell>{lead.source || t('crm.unknown')}</TableCell>
                    <TableCell>{date(lead.createdAt)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <CrmPagination
            page={page}
            total={leads.data.total}
            pageSize={leads.data.pageSize}
            onPage={setPage}
          />
        </>
      )}
    </>
  );
};
